import React, { useEffect, useRef, useState } from 'react';
import '../../styles/webinar.css';
import { Link } from 'react-router-dom';
import { CALENDAR_URL, DEFAULT_CONTENT, loadWebinarContent } from './webinarContent';

// Diligentsia Founder Series — session 1 registration page. Ported from Irij's "Founder Roundtable"
// design handoff, which styles almost everything inline; the few base rules live in webinar.css.
//
// Three documents govern this page and they do not all say the same thing:
//   - the design decides layout and wording;
//   - Webinar-Developer-Brief-Page-And-Form.docx decides what the form sends (field names, radio
//     values, HubSpot wiring, confirmation state, sticky mobile CTA) and overrides the design;
//   - Webinar-HOUSE-FACTS.docx is the source of truth for names, titles and the palette, so the
//     colours below are its values rather than the design's. Typography is the exception: the
//     design's two faces are kept, because matching it on Poppins alone moves every line break.
const LOGO = require('../../images/logo.png');
const HERO = require('../../images/landing-pages/webinar-assets/hero-lockup.png');
const NEIL = require('../../images/landing-pages/webinar-assets/foster-neil-bio.png');
const PAUL = require('../../images/landing-pages/webinar-assets/paul-morris.jpg');
const PAUL_QUOTE = require('../../images/landing-pages/webinar-assets/paul-morris-quote.jpg');
const OLLIE = require('../../images/landing-pages/webinar-assets/ollie-austen.jpg');
const MARK = require('../../images/landing-pages/webinar-assets/call-button-mark.png');

// House Facts brand tokens. The design used #FC7785 / #4A67FF, so the palette below is House
// Facts' reconciled values rather than the design's.
const PINK = '#FF4F6D';
const BLUE = '#4D79FF';
const INK = '#0A0A0F';
const OFFWHITE = '#F4F4F6';
// Typography follows the design instead: Space Grotesk sets every display heading and Poppins
// (standing in for the design's Inter, per House Facts) sets body copy, labels and form fields.
// Reason: the two faces differ in width, so swapping DISPLAY for FONT also moves every heading's
// line breaks away from the design's — they are not interchangeable.
const FONT = "'Poppins',sans-serif";
const DISPLAY = "'Space Grotesk',sans-serif";

// HubSpot passive capture. The account is on EU data hosting, so the loader must stay pointed at
// js-eu1 — the generic snippet from HubSpot's docs will not work. Portal id from the integration
// brief. Reason: no HubSpot tracking exists anywhere else in this app, but the id is checked
// before injecting anyway, because two loaders on one page break form detection.
const HUBSPOT_PORTAL_ID = '147324908';
const HUBSPOT_SCRIPT_ID = 'hs-script-loader';

// Neil's title line, as the design writes it. House Facts gives it as "Co-Chair, Global Technology
// Group · Brown Rudnick" instead; both satisfy its FLAG against leading with "Partner at Brown
// Rudnick", and the design wins here per the instruction to match it. Worth a confirmation with
// Maham before go-live, since House Facts is the later document.
const NEIL_TITLE = 'Global Co-Chair of Technology Sector · Brown Rudnick LLP';

// The four radio values are a BLOCKER in the developer brief: HubSpot segments the whole email
// sequence on them, and a casing or wording mismatch silently routes fifteen emails to nobody.
const ICP_OPTIONS = [
    { value: 'founder', label: "I'm raising for my own company" },
    { value: 'advisor', label: 'I advise companies through raises' },
    { value: 'investor', label: 'I invest in companies' },
    { value: 'other', label: 'Something else' },
];

const dot = { color: '#3a3a44' };
const lightDot = { color: '#5a5a66' };
const fieldStyle = { background: 'transparent', border: '1px solid #3a3a44', color: OFFWHITE, padding: '11px 13px', fontSize: '14px' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: '7px', font: `600 12px ${FONT}`, color: OFFWHITE };
const radioStyle = { display: 'flex', alignItems: 'center', gap: '9px', border: '1px solid #3a3a44', borderRadius: '14px', padding: '11px 13px', font: `13px ${FONT}`, color: '#c9c9d2' };
const chipStyle = { font: `600 11px ${FONT}`, padding: '7px 12px', borderRadius: '9999px', background: '#fff', border: '1px solid #C4C4FF80', color: '#2E4ED8' };
const stepNum = { font: `600 11px ${FONT}`, color: PINK, flex: 'none' };
const stepText = { font: `600 15.5px ${FONT}`, color: INK };

/**
 * Injects the HubSpot tracking loader once, immediately before </body>.
 * Removing it on unmount would leave HubSpot's own globals behind, so the script is left in place
 * and re-mounts are guarded by the element id instead.
 */
const useHubspotTracking = () => {
    useEffect(() => {
        if (document.getElementById(HUBSPOT_SCRIPT_ID)) return;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = HUBSPOT_SCRIPT_ID;
        script.async = true;
        script.defer = true;
        script.src = `//js-eu1.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
        document.body.appendChild(script);
    }, []);
};

/** Shows the sticky mobile CTA once the hero has scrolled out of view. */
const useStickyCta = (heroRef) => {
    const [isStuck, setIsStuck] = useState(false);
    useEffect(() => {
        const hero = heroRef.current;
        if (!hero || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), { threshold: 0 });
        observer.observe(hero);
        return () => observer.disconnect();
    }, [heroRef]);
    return isStuck;
};

const WebinarLanding = () => {
    const [content, setContent] = useState(DEFAULT_CONTENT);
    const [isRegistered, setIsRegistered] = useState(false);
    const heroRef = useRef(null);
    const isStuck = useStickyCta(heroRef);

    useHubspotTracking();

    useEffect(() => {
        let isMounted = true;
        loadWebinarContent().then((resolved) => { if (isMounted) setContent(resolved); });
        return () => { isMounted = false; };
    }, []);

    // Reason: the developer brief marks this a BLOCKER — HubSpot's own listener is on the same
    // submit event, so preventDefault() alone is allowed and stopPropagation() is not. Without
    // preventDefault the page reloads; with stopPropagation the submission silently never lands.
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsRegistered(true);
    };

    const { sessionTitle, heroSubtitle, heroAudience, sessionDate, sessionTime, session2Title, session3Title, joiningLink } = content;
    const meta = `${sessionDate} · ${sessionTime} · Online · Free`;

    return (
        <div className="dlg-webinar">
            <div>
                <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '32px', maxWidth: '1120px', margin: '0 auto', padding: '24px 48px', borderBottom: '1px solid #e4e4e9' }}>
                    <Link to="/"><img src={LOGO} alt="Diligentsia" style={{ height: '26px' }} /></Link>
                    <div style={{ font: `600 10px ${FONT}`, letterSpacing: '.14em', textTransform: 'uppercase', color: '#6b6b76' }}>
                        WEBINAR SERIES
                    </div>
                </header>

                <section ref={heroRef} style={{ background: INK, color: OFFWHITE, position: 'relative', overflow: 'hidden', marginBottom: 0 }}>
                    <div aria-hidden="true" style={{ position: 'absolute', top: '-160px', right: '-160px', width: '540px', height: '540px', borderRadius: '50%', background: 'rgba(77,121,255,0.40)', filter: 'blur(80px)', pointerEvents: 'none' }}>
                    </div>
                    <div className="wrap" style={{ padding: '80px 48px 88px', position: 'relative', zIndex: '1' }}>
                        <div className="web-hero-grid">
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
                                    <span style={{ font: `600 11px ${FONT}`, letterSpacing: '.16em', color: PINK }}>
                                        SESSION 1 OF 3 · FREE
                                    </span>
                                </div>
                                <h1 style={{ font: `400 clamp(28px, 3.6vw, 44px)/1.14 ${DISPLAY}`, maxWidth: '27.3ch', marginBottom: '44px' }}>
                                    {sessionTitle}
                                </h1>
                                <div style={{ font: `400 18px/1.6 ${FONT}`, color: '#c9c9d2', maxWidth: '56ch' }}>
                                    {heroSubtitle}
                                </div>
                                <div style={{ font: `400 15px/1.6 ${FONT}`, color: '#a8a8b4', maxWidth: '56ch', marginTop: '18px' }}>
                                    {heroAudience}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', margin: '32px 0', font: `600 13px ${FONT}`, color: '#e8e8ec' }}>
                                    <span>{sessionDate}</span>
                                    <span style={dot}>·</span>
                                    <span>{sessionTime}</span>
                                    <span style={dot}>·</span>
                                    <span>Online</span>
                                    <span style={dot}>·</span>
                                    <span style={{ padding: '5px 12px', borderRadius: '9999px', background: 'rgba(255,79,109,0.16)', color: PINK }}>
                                        Free
                                    </span>
                                </div>
                                <a href="#register" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', background: BLUE, color: '#fff', font: `600 15px ${FONT}`, padding: '15px 26px', borderRadius: '9999px', textDecoration: 'none' }}>
                                    Reserve your place →
                                </a>
                            </div>
                            {/* The lockup sits on its own flat black card, so the hero's blue glow passes
                                behind rather than across it — which is what makes the card read as a card. */}
                            <div className="web-hero-card">
                                <img src={HERO} alt="Diligentsia in collaboration with Brown Rudnick" style={{ width: '76%', maxHeight: '300px', objectFit: 'contain' }} />
                                <div style={{ font: `13px/1.5 ${FONT}`, color: '#9a9aa6', textAlign: 'center' }}>
                                    In collaboration with Brown Rudnick and Startup 2 Standup
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section style={{ borderTop: '1px solid #e4e4e9', backgroundColor: '#FFFFFF', marginBottom: 0 }}>
                    <div className="wrap web-paul-quote" style={{ padding: '64px 48px', gap: '24px 40px', alignItems: 'center', maxWidth: '1024px' }}>
                        <img src={PAUL_QUOTE} alt="Paul Morris" style={{ width: '150px', height: '150px', borderRadius: '8px', objectFit: 'cover' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <span style={{ font: `600 30px/.7 ${DISPLAY}`, color: BLUE }}>“</span>
                            <div style={{ font: `500 17px/1.5 ${FONT}`, color: INK }}>
                                Addressing the issues that Neil will talk about ahead of time will save you time and cost.
                            </div>
                            <div style={{ borderTop: '1px solid #e4e4e9', paddingTop: '10px', maxWidth: '280px' }}>
                                <div style={{ font: `600 12px ${FONT}`, color: INK }}>Paul Morris</div>
                                <div style={{ font: `11px/1.5 ${FONT}`, color: '#6b6b76', marginTop: '2px' }}>Ex-Head of Growth Advisory, BDO</div>
                            </div>
                        </div>
                    </div>
                </section>

                <section style={{ borderTop: '1px solid #e4e4e9', backgroundColor: '#F5F6FF', marginBottom: 0 }}>
                    <div className="wrap web-two-col" style={{ padding: '96px 48px', gap: '48px 72px', alignItems: 'start' }}>
                        <div style={{ font: `600 clamp(26px, 5vw, 40px)/1.15 ${DISPLAY}`, maxWidth: '16.6ch' }}>
                            Most raises and exits don't fall apart at the pitch.
                        </div>
                        <div>
                            <div style={{ font: `16px/1.65 ${FONT}`, color: '#353b4d' }}>
                                The pitch gets you into the room. Then the questions become more detailed.
                            </div>
                            <div style={{ borderLeft: `2px solid ${PINK}`, paddingLeft: '20px', font: `600 clamp(17px, 2.2vw, 19px)/1.5 ${DISPLAY}`, color: INK, marginTop: '24px' }}>
                                Who owns what? Which rights have been granted? Are the contracts in order? Was that promise to a shareholder ever documented? Has the IP actually been assigned?
                            </div>
                            <div style={{ font: `14.5px/1.65 ${FONT}`, color: '#4a4a55', marginTop: '24px' }}>
                                These are the details that come under scrutiny during due diligence — and they can turn into questions, delays or renegotiation.
                            </div>
                            <div style={{ font: `600 14.5px/1.65 ${FONT}`, color: INK, marginTop: '14px' }}>
                                The earlier you find them, the more options you have to deal with them.
                            </div>
                        </div>
                    </div>
                </section>

                <section style={{ background: '#fff', borderTop: '1px solid #e4e4e9' }}>
                    <div className="wrap" style={{ padding: '96px 48px' }}>
                        <div style={{ font: `600 11px ${FONT}`, letterSpacing: '.16em', color: '#6b6b76', marginBottom: '20px' }}>
                            WHO YOU'LL HEAR FROM
                        </div>
                        <div style={{ font: `400 clamp(24px, 4vw, 34px)/1.15 ${DISPLAY}`, maxWidth: '39.1ch', marginBottom: '48px' }}>
                            Perspectives from across the raise or exit process.
                        </div>
                        <div style={{ borderTop: '1px solid #d3d3db', padding: '28px 0', display: 'flex', flexWrap: 'wrap', gap: '24px 32px', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: '0 0 200px' }}>
                                <img src={PAUL} alt="Paul Morris" style={{ width: '72px', height: '72px', flex: 'none', borderRadius: '50%', objectFit: 'cover' }} />
                                <div>
                                    <div style={{ font: `600 15px ${FONT}` }}>Paul Morris</div>
                                    <div style={{ font: `600 14px ${FONT}`, color: BLUE, marginTop: '3px', width: '137px' }}>Ex-Head of Growth Advisory, BDO</div>
                                    <div style={{ font: `11px/1.5 ${FONT}`, color: '#6b6b76', marginTop: '3px' }}>Former Partner, Livingbridge VC</div>
                                </div>
                            </div>
                            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                                <div style={{ font: `500 18px/1.5 ${FONT}`, color: INK, maxWidth: '903px' }}>
                                    "Not being prepared and a lack of data integrity can lead to price chipping, but actually more often causes deals to fail. Anything you can do to reduce this is immensely valuable."
                                </div>
                                <div style={{ font: `13px/1.6 ${FONT}`, color: '#6b6b76', marginTop: '14px' }}>
                                    Paul brings an investor and growth-advisory perspective, having worked with growth businesses as an investor, banker, non-executive director and adviser.
                                </div>
                            </div>
                        </div>

                        <div style={{ font: `600 10.5px ${FONT}`, letterSpacing: '.14em', textTransform: 'uppercase', color: BLUE, margin: '32px 0 20px' }}>
                            Led by Neil Foster
                        </div>
                        <div style={{ border: '1px solid #C4C4FF80', borderRadius: '20px', padding: '36px', display: 'flex', flexWrap: 'wrap', gap: '32px 40px', alignItems: 'flex-start', background: '#F5F6FF' }}>
                            {/* The handoff's <image-slot> carries `aspect-ratio: 3/2` in its shadow root, so this
                                slot resolves to 420x280 and overflows the 260px flex basis written on its wrapper.
                                Its frame also paints rgba(127,127,127,.08) behind the image, which is what the
                                cut-out portrait sits on. Both are reproduced here rather than the wrapper's basis. */}
                            <div className="web-neil-photo">
                                <img src={NEIL} alt="Neil Foster" style={{ display: 'block', width: '100%', height: '280px', borderRadius: '20px', objectFit: 'cover', background: 'rgba(127,127,127,.08)' }} />
                            </div>
                            <div style={{ flex: '1 1 340px', minWidth: 0 }}>
                                <div style={{ font: `400 clamp(24px, 3.4vw, 30px) ${DISPLAY}` }}>Neil Foster</div>
                                <div style={{ font: `600 14px ${FONT}`, color: BLUE, marginTop: '3px' }}>{NEIL_TITLE}</div>
                                <div style={{ font: `13.5px/1.6 ${FONT}`, color: '#2a2a33', marginTop: '14px' }}>
                                    Neil co-chairs Brown Rudnick's Global Technology group and is a lead partner in Emerging Growth Companies &amp; Venture Capital, with 25 years advising on cross-border transactions.
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '19px' }}>
                                    <span style={chipStyle}>Chambers UK · Corporate/M&amp;A since 2010</span>
                                    <span style={chipStyle}>Legal 500 · Leading Partner, Venture Capital</span>
                                    <span style={{ ...chipStyle, border: '1px solid #d3d3db', color: '#4a4a55' }}>Author · Masters of Raising Venture Capital, 2024</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '32px', paddingTop: '32px', display: 'flex', flexWrap: 'wrap', gap: '32px 40px', alignItems: 'center' }}>
                            <img src={OLLIE} alt="Ollie Austen" style={{ width: '88px', height: '88px', flex: 'none', borderRadius: '50%', objectFit: 'cover' }} />
                            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                                <div style={{ marginTop: '14px' }}>
                                    <div style={{ font: `600 15px ${FONT}` }}>Ollie Austen</div>
                                    <div style={{ font: `11px ${FONT}`, color: '#6b6b76', marginTop: '3px' }}>Founder, Startup 2 Standup</div>
                                    <div style={{ font: `13px/1.6 ${FONT}`, color: '#6b6b76', marginTop: '14px' }}>
                                        Ollie brings the founder perspective and his experience supporting entrepreneurs through Startup to Standup
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section style={{ backgroundColor: '#F5F6FF', marginBottom: 0 }}>
                    <div className="wrap web-mark-quote" style={{ padding: '48px 48px 96px', borderTop: '1px solid #e4e4e9', gap: '32px 48px', alignItems: 'center' }}>
                        <div>
                            <div style={{ font: `500 clamp(18px, 2.4vw, 22px)/1.5 ${FONT}`, color: INK }}>
                                "Neil is one of the UK's most active transaction lawyers. I've been doing deals for 30 years and still learn every time he speaks. So will you. He is a highly entertaining speaker and not to be missed."
                            </div>
                            <div style={{ font: `13.5px/1.5 ${FONT}`, color: '#6b6b76', marginTop: '20px' }}>
                                <div style={{ font: `600 13.5px ${FONT}`, color: INK }}>Mark Bernstein</div>
                                <div style={{ marginTop: '4px' }}>Founder &amp; CEO, Diligentsia</div>
                            </div>
                        </div>
                        <img src={MARK} alt="Mark Bernstein" style={{ width: '130px', height: '130px', borderRadius: '50%', objectFit: 'cover' }} />
                    </div>
                </section>

                <section id="register" style={{ background: INK, color: OFFWHITE, borderTop: '1px solid #e4e4e9', marginBottom: 0 }}>
                    <div className="wrap web-register-grid" style={{ padding: '96px 48px', gap: '48px 64px', alignItems: 'start' }}>
                        <div>
                            <div style={{ font: `400 clamp(24px, 4vw, 34px)/1.15 ${DISPLAY}` }}>Reserve your place</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', font: `15.5px/1.6 ${FONT}`, color: '#c9c9d2', marginTop: '20px' }}>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <span style={{ color: '#7B93FF' }}>✓</span>
                                    <span>If there's something you'd like Neil to cover, feel free to submit a question.</span>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <span style={{ color: '#7B93FF' }}>✓</span>
                                    <span>Can't make it live? Register anyway and tick the box for the recording.</span>
                                </div>
                            </div>
                            <div style={{ borderTop: '1px solid #3a3a44', marginTop: '28px', paddingTop: '20px', font: `600 12.5px ${FONT}`, color: '#c9c9d2', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <span>{sessionDate}</span>
                                <span style={lightDot}>·</span>
                                <span>{sessionTime}</span>
                                <span style={lightDot}>·</span>
                                <span>Online</span>
                                <span style={lightDot}>·</span>
                                <span>Free</span>
                            </div>
                        </div>

                        {isRegistered ? (
                            // Reason: HubSpot's confirmation email can take minutes to arrive, so the on-page
                            // state has to carry the joining link and the calendar link itself — otherwise
                            // people assume the form failed and submit again.
                            <div role="status" aria-live="polite" style={{ border: '1px solid #3a3a44', borderRadius: '22px', padding: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ font: `400 clamp(24px, 3vw, 28px)/1.2 ${DISPLAY}`, color: OFFWHITE }}>You're registered.</div>
                                <div style={{ font: `15.5px/1.6 ${FONT}`, color: '#c9c9d2' }}>{sessionDate}, {sessionTime}.</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
                                    <a href={CALENDAR_URL} style={{ background: BLUE, color: '#fff', font: `600 14px ${FONT}`, padding: '13px 22px', borderRadius: '9999px', textDecoration: 'none' }}>
                                        Add to your calendar
                                    </a>
                                    {joiningLink ? (
                                        <a href={joiningLink} target="_blank" rel="noreferrer" style={{ background: 'transparent', border: '1px solid #3a3a44', color: OFFWHITE, font: `600 14px ${FONT}`, padding: '12px 22px', borderRadius: '9999px', textDecoration: 'none' }}>
                                            Join on the day
                                        </a>
                                    ) : null}
                                </div>
                                <div style={{ font: `13px/1.6 ${FONT}`, color: '#8f8f9b' }}>
                                    A confirmation email is on its way with the same links.
                                </div>
                            </div>
                        ) : (
                            <form id="webinarRegisterForm" onSubmit={handleSubmit} style={{ background: 'transparent', borderRadius: '22px', padding: '22px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <div className="web-field-row">
                                    <label style={labelStyle}>
                                        First name
                                        <input type="text" name="firstname" required style={fieldStyle} />
                                    </label>
                                    <label style={labelStyle}>
                                        Last name
                                        <input type="text" name="lastname" required style={fieldStyle} />
                                    </label>
                                </div>
                                <div className="web-field-row">
                                    <label style={labelStyle}>
                                        Email
                                        <input type="email" name="email" required style={fieldStyle} />
                                    </label>
                                    <label style={labelStyle}>
                                        Company (optional)
                                        <input type="text" name="company" style={fieldStyle} />
                                    </label>
                                </div>
                                <div>
                                    <div style={{ font: `600 12px ${FONT}`, marginBottom: '10px', color: OFFWHITE }}>
                                        Which is closest to you?
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                                        {ICP_OPTIONS.map(({ value, label }) => (
                                            <label key={value} htmlFor={`icp-${value}`} style={radioStyle}>
                                                <input type="radio" id={`icp-${value}`} name="series_icp" value={value} required />
                                                {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <label style={labelStyle}>
                                    Anything you'd like Neil to cover? (optional)
                                    <textarea rows="3" name="series_question" style={{ ...fieldStyle, resize: 'vertical', fontFamily: FONT }} />
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', font: `13.5px ${FONT}`, color: '#c9c9d2' }}>
                                    <input type="checkbox" name="series_recording_only" />
                                    I can't make it live — send me the recording
                                </label>
                                <button type="submit" style={{ width: '100%', background: BLUE, color: '#fff', font: `600 15px ${FONT}`, padding: '15px', border: 'none', cursor: 'pointer', borderRadius: '9999px' }}>
                                    Reserve your place →
                                </button>
                                <div style={{ font: `12px/1.5 ${FONT}`, color: '#8f8f9b', textAlign: 'center' }}>
                                    Takes under a minute. We'll send the joining link and the checklist — nothing else.
                                </div>
                            </form>
                        )}
                    </div>
                </section>

                <section style={{ backgroundColor: '#F5F6FF', marginBottom: 0 }}>
                    <div className="wrap web-two-col" style={{ padding: '80px 48px', gap: '40px 64px', alignItems: 'center' }}>
                        <div>
                            <div style={{ font: `500 clamp(24px, 4vw, 32px)/1.2 ${DISPLAY}`, maxWidth: '22.4ch', color: INK }}>
                                Put the session into practice with Diligentsia
                            </div>
                            <div style={{ font: `15px/1.6 ${FONT}`, color: '#4a4a55', maxWidth: '47.1ch', marginTop: '22px' }}>
                                The issues covered in the session are easier to address when you know what to look for. Diligentsia's free online checklist gives you a practical way to work through them against your own business.
                            </div>
                        </div>
                        <div>
                            <div style={{ display: 'flex', gap: '18px', alignItems: 'baseline', padding: '18px 0', borderTop: '1px solid #C4C4FF80' }}>
                                <span style={stepNum}>01</span>
                                <span style={stepText}>Learn what to look for.</span>
                            </div>
                            <div style={{ display: 'flex', gap: '18px', alignItems: 'baseline', padding: '18px 0', borderTop: '1px solid #C4C4FF80' }}>
                                <span style={stepNum}>02</span>
                                <span style={stepText}>Sign in with Free Diligence Account.</span>
                            </div>
                            <div style={{ display: 'flex', gap: '18px', alignItems: 'baseline', padding: '18px 0', borderTop: '1px solid #C4C4FF80', borderBottom: '1px solid #C4C4FF80' }}>
                                <span style={stepNum}>03</span>
                                <span style={stepText}>Use Diligentsia's free checklist to apply the learning.</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section style={{ background: '#fff', borderTop: '1px solid #e4e4e9', marginBottom: 0 }}>
                    <div className="wrap" style={{ padding: '88px 48px' }}>
                        <div className="web-two-col" style={{ gap: '24px 64px', alignItems: 'start', marginBottom: '44px' }}>
                            <div style={{ font: `500 clamp(23px, 3.6vw, 30px)/1.2 ${DISPLAY}` }}>More sessions are coming.</div>
                            <div style={{ font: `14.5px/1.6 ${FONT}`, color: '#4a4a55' }}>
                                Session 1 is the first in a series of practical conversations for founders navigating growth, a raise or exit, and what happens beyond the pitch.
                            </div>
                        </div>
                        <div style={{ maxWidth: '820px', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 28px', alignItems: 'center', background: '#F5F6FF', border: '1px solid #C4C4FF80', borderRadius: '18px', padding: '20px 24px' }}>
                                <div style={{ font: `600 12px ${FONT}`, color: INK, flex: '0 0 110px' }}>01 · September</div>
                                <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
                                    <span style={{ font: `600 15.5px ${FONT}`, color: INK }}>{sessionTitle}</span>
                                    <span style={{ font: `600 10.5px ${FONT}`, letterSpacing: '.06em', textTransform: 'uppercase', padding: '5px 11px', borderRadius: '9999px', background: '#FEE3E7', color: '#F25A6E', flex: 'none' }}>
                                        You are here
                                    </span>
                                </div>
                            </div>
                            <div className="web-session-row" style={{ borderTop: '1px solid #e4e4e9', padding: '18px 24px' }}>
                                <div style={{ font: `600 12px ${FONT}`, color: '#9a9aa6' }}>02 · October</div>
                                <span style={{ font: `14.5px ${FONT}`, color: '#9a9aa6' }}>{session2Title}</span>
                            </div>
                            <div className="web-session-row" style={{ borderTop: '1px solid #e4e4e9', padding: '18px 24px' }}>
                                <div style={{ font: `600 12px ${FONT}`, color: '#9a9aa6' }}>03 · November</div>
                                <span style={{ font: `14.5px ${FONT}`, color: '#9a9aa6' }}>{session3Title}</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section style={{ background: BLUE, borderTop: '1px solid #e4e4e9', marginBottom: 0 }}>
                    <div className="wrap" style={{ padding: '64px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '32px', flexWrap: 'wrap' }}>
                        <div style={{ maxWidth: '56ch' }}>
                            <div style={{ font: `600 clamp(18px, 2.4vw, 21px)/1.3 ${DISPLAY}`, color: '#fff' }}>{sessionTitle}</div>
                            <div style={{ font: `13.5px ${FONT}`, color: '#E4E9FF', marginTop: '10px' }}>{meta}</div>
                        </div>
                        <a href="#register" style={{ background: '#fff', color: BLUE, font: `600 14px ${FONT}`, padding: '14px 24px', borderRadius: '9999px', textDecoration: 'none' }}>
                            Reserve your place →
                        </a>
                    </div>
                </section>

                <footer style={{ background: INK, color: '#8f8f9b' }}>
                    <div className="wrap" style={{ padding: '32px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap', font: `12px ${FONT}` }}>
                        <img src={LOGO} alt="Diligentsia" style={{ height: '20px' }} />
                        <span>Hosted by Diligentsia, in collaboration with Brown Rudnick and Startup 2 Standup</span>
                    </div>
                </footer>

                {/* Phone traffic arrives from a LinkedIn post and the hero button is its whole journey, so
                    the brief requires it to persist as a slim bar once the hero scrolls away. */}
                {!isRegistered && (
                    <a href="#register" className={`web-sticky-cta${isStuck ? ' is-visible' : ''}`} style={{ background: BLUE, color: '#fff', font: `600 15px ${FONT}` }}>
                        Reserve your place →
                    </a>
                )}
            </div>
        </div>
    );
};

export default WebinarLanding;
